import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { PageProps } from "../types/auth";

import styles from './styles/LevelDetailsPage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Skill {
  skill_id: number;
  name: string;
}

interface Level {
  level_id: number;
  name: string;
  skills: Skill[];
}

interface RouteParams {
  [key: string]: string | undefined;
  level_id?: string;
}

interface LocationState {
  curriculum_id?: string;
}

const LevelDetailsPage: React.FC<PageProps> = ({ token, selectedOrgID }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { level_id } = useParams<RouteParams>();

  const [curriculum_id, setCurriculumID] = useState<string | null>(null);
  const [name, setName] = useState<string>("");
  const [skills, setSkills] = useState<Skill[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<Set<number>>(new Set());
  const [newSkillName, setNewSkillName] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    const state = location.state as LocationState;
    setCurriculumID(state?.curriculum_id || null);
  }, [location.state]);

  useEffect(() => {
    const fetchSkills = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/skills`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch skills.");
        }

        const data: Skill[] = await response.json();
        setSkills(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An unexpected error occurred");
      }
    };

    const fetchLevelDetails = async () => {
      if (!level_id) return;
      try {
        const response = await fetch(`${API_BASE_URL}/api/levels/${level_id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch level details.");
        }

        const data: Level = await response.json();
        setName(data.name || "");
        setSelectedSkills(new Set((data.skills || []).map((skill) => skill.skill_id)));
      } catch (err) {
        setError(err instanceof Error ? err.message : "An unexpected error occurred");
      }
    };

    fetchSkills();
    fetchLevelDetails();
  }, [token, selectedOrgID, level_id]);

  const handleSkillSelect = async (skillID: number) => {
    try {
      const isSelected = selectedSkills.has(skillID);
      if (isSelected) {
        // Remove skill association
        const response = await fetch(
          `${API_BASE_URL}/api/levels/${level_id}/skills/${skillID}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
              "X-Org-ID": selectedOrgID?.toString() || "",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to remove skill from level");
        }
      } else {
        // Add skill association
        const response = await fetch(
          `${API_BASE_URL}/api/levels/${level_id}/skills`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
              "X-Org-ID": selectedOrgID?.toString() || "",
            },
            body: JSON.stringify({ skill_id: skillID }),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to add skill to level");
        }
      }

      // Update local state only after successful API call
      setSelectedSkills((prev) => {
        const updated = new Set(prev);
        if (isSelected) {
          updated.delete(skillID);
        } else {
          updated.add(skillID);
        }
        return updated;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "An unexpected error occurred");
    }
  };

  const handleCreateSkill = async () => {
    if (!newSkillName.trim()) {
      setError("Skill name cannot be empty.");
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/skills`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
        body: JSON.stringify({
          org_id: selectedOrgID ? parseInt(selectedOrgID.toString(), 10) : 0,
          name: newSkillName,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Failed to create skill.");
        return;
      }

      const newSkill: Skill = await response.json();
      setSkills((prevSkills) => [...prevSkills, newSkill]);
      setSelectedSkills((prev) => new Set(prev).add(newSkill.skill_id));
      setNewSkillName("");
    } catch (err) {
      setError("An unexpected error occurred while creating the skill.");
    }
  };

  const handleSaveOrUpdateLevel = async () => {
    if (!name.trim()) {
      setError("Level name cannot be empty.");
      return;
    }

    try {
      let levelResponse;
      const curriculumIdInt = curriculum_id ? parseInt(curriculum_id, 10) : 0;

      if (level_id) {
        levelResponse = await fetch(`${API_BASE_URL}/api/levels/${level_id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
          body: JSON.stringify({
            curriculum_id: curriculumIdInt,
            name,
          }),
        });
      } else {
        levelResponse = await fetch(`${API_BASE_URL}/api/levels`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
          body: JSON.stringify({
            curriculum_id: curriculumIdInt,
            name,
          }),
        });
      }

      if (!levelResponse.ok) {
        const errorData = await levelResponse.json();
        setError(errorData.error || "Failed to save level.");
        return;
      }

      const level: Level = await levelResponse.json();
      if (level_id) {
        navigate(`/curriculums/${curriculum_id}`);
      } else {
        navigate(`/levels/${level.level_id}`, {
          state: {
            curriculum_id,
          }
        });
      }
    } catch (err) {
      setError("An unexpected error occurred.");
    }
  };

  const handleDeleteLevel = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/levels/${level_id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Failed to delete level.");
        return;
      }

      navigate(`/curriculums/${curriculum_id}`);
    } catch (err) {
      setError("An unexpected error occurred while deleting the level.");
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>{level_id ? "Level Details" : "New Level"}</h1>
      {error && <p className={styles.errorMessage}>{error}</p>}
      <div className={styles.formGroup}>
        <div className={styles.inputGroup}>
          <label className={styles.label}>Level Name:</label>
          <input
            type="text"
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            required
            className={styles.input}
          />
        </div>
      </div>
      <div className={styles.buttonGroup}>
        <button onClick={handleSaveOrUpdateLevel} className={styles.submitButton}>
          {level_id ? "Update Level" : "Create Level"}
        </button>
        {level_id && (
          <button 
            onClick={() => setShowDeleteConfirm(true)} 
            className={styles.deleteButton}
          >
            Delete Level
          </button>
        )}
      </div>

      {showDeleteConfirm && (
        <>
          <div className={styles.dialogOverlay} onClick={() => setShowDeleteConfirm(false)} />
          <div className={styles.dialog}>
            <p>
              Delete level {name}?<br /><br />
              This action CANNOT be undone.
            </p>
            <div className={styles.dialogButtons}>
              <button onClick={handleDeleteLevel} className={styles.dialogButton}>
                Yes, Delete
              </button>
              <button onClick={() => setShowDeleteConfirm(false)} className={styles.dialogCancelButton}>
                Cancel
              </button>
            </div>
          </div>
        </>
      )}

      {level_id && (
        <div className={styles.skillsSection}>
          <h2 className={styles.skillsTitle}>Skills</h2>
          
          <div className={styles.formGroup}>
            <input
              type="text"
              id="search-skills"
              placeholder="Search skills..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.input}
            />
          </div>

          {/* New Skill Form */}
          <div className={styles.formGroup}>
            <input
              type="text"
              value={newSkillName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewSkillName(e.target.value)}
              placeholder="New skill name"
              className={styles.input}
            />
            <button onClick={handleCreateSkill} className={styles.submitButton}>Create New Skill</button>
          </div>

          {/* Skills Table */}
          <table className={styles.skillsTable}>
            <thead>
              <tr>
                <th className={styles.tableHeader}>Skill Name</th>
                <th className={styles.tableHeader}>Select</th>
              </tr>
            </thead>
            <tbody>
              {skills
                .filter((skill) => skill.name.toLowerCase().includes(searchTerm.toLowerCase()))
                .sort((a, b) => {
                  // Sort by selection status only
                  const aSelected = selectedSkills.has(a.skill_id);
                  const bSelected = selectedSkills.has(b.skill_id);
                  return aSelected ? -1 : bSelected ? 1 : 0;
                })
                .map((skill) => (
                <tr key={skill.skill_id} className={styles.tableRow}>
                  <td className={styles.tableCell}>{skill.name}</td>
                  <td className={styles.tableCell}>
                    <input
                      type="checkbox"
                      checked={selectedSkills.has(skill.skill_id)}
                      onChange={() => handleSkillSelect(skill.skill_id)}
                      className={styles.checkbox}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LevelDetailsPage;
